import { memo } from 'react';
import { Box, Heading, Image, Tag, Text } from '@zillow/constellation-10';
import HomeRentalSvg from './HomeRentalSvg';
import type { Address, YourRentalListingSectionDto, YourRentalStatusTag } from '@zg-rentals/ts-your-rental-api-client';
import { TAG_APPEARANCE_MAP } from '../../constants';

const CONTENT = {
  TITLE: 'Your rental',
} as const;

type PropertyCardProps = {
  address?: Address;
  photoThumbUrl?: string;
  contactIreneModal?: React.ReactNode;
  statusTag?: YourRentalStatusTag;
};

type PropertySectionProps = {
  listingSection: YourRentalListingSectionDto;
  hasMultipleRentals: boolean;
  contactIreneModal?: React.ReactNode;
  viewRentalsModal?: React.ReactNode;
  addRentalModal?: React.ReactNode;
};

const PropertyCard = memo(({ address, photoThumbUrl, contactIreneModal, statusTag }: PropertyCardProps) => {
  return (
    <Box
      css={{
        display: 'flex',
        gap: 'default',
      }}
    >
      <Box
        css={{
          display: 'flex',
          width: { base: '138px', lg: '188px' },
          justifyContent: 'center',
          backgroundColor: 'var(--bg-softest, #EEEEF0)',
          borderRadius: '{radii.500}',
          overflow: 'hidden',
        }}
      >
        {photoThumbUrl ? (
          <Image
            src={photoThumbUrl}
            alt="Property thumbnail"
            css={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <HomeRentalSvg />
        )}
      </Box>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 'tight',
        }}
      >
        {statusTag && (
          <Tag tone={TAG_APPEARANCE_MAP[statusTag.tagAppearance]} size="sm">
            {statusTag.tagText}
          </Tag>
        )}
        {address && (
          <>
            <Text
              data-testid="Header-AddressStreet"
              css={{
                textStyle: { base: 'heading-sm', lg: 'heading-md' },
              }}
            >
              {address.street}
              {address.unit ? ` #${address.unit}` : ''}
            </Text>
            <Text data-testid="Header-AddressCityStateZip" textStyle="body-lg">
              {address.city}, {address.state} {address.zip}
            </Text>
          </>
        )}
        {contactIreneModal}
      </Box>
    </Box>
  );
});

PropertyCard.displayName = 'PropertyCard';

type ModalActionsProps = {
  hasMultipleRentals: boolean;
  viewRentalsModal?: React.ReactNode;
  addRentalModal?: React.ReactNode;
};

export const ModalActions = memo(({ hasMultipleRentals, viewRentalsModal, addRentalModal }: ModalActionsProps) => {
  return (
    <Box
      css={{
        display: 'flex',
        gap: 'tighter',
      }}
    >
      {addRentalModal}
      {hasMultipleRentals && viewRentalsModal}
    </Box>
  );
});

ModalActions.displayName = 'ModalActions';

const PropertySection = memo(
  ({
    listingSection,
    hasMultipleRentals,
    contactIreneModal,
    viewRentalsModal,
    addRentalModal,
  }: PropertySectionProps) => {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'default',
          width: '100%',
        }}
      >
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Heading data-testid="Header-Title" fontFamily="heading" level={1} textStyle="heading-sm">
            {CONTENT.TITLE}
          </Heading>
          <ModalActions
            hasMultipleRentals={hasMultipleRentals}
            viewRentalsModal={viewRentalsModal}
            addRentalModal={addRentalModal}
          />
        </Box>
        <PropertyCard
          address={listingSection.address}
          photoThumbUrl={listingSection.photoThumbUrl}
          contactIreneModal={contactIreneModal}
          statusTag={listingSection.statusTag}
        />
      </Box>
    );
  },
);

PropertySection.displayName = 'PropertySection';

export default PropertySection;
